import { ChevronDown, ChevronRight, Plus, User } from 'lucide-react';
import { Decimal, format as formatMoney } from '@mlm/shared';
import { cn } from '../lib/cn';
import { displayRankCode } from './rank-display';
import type {
  AddSlotContext,
  GenealogyNodeFields,
  GenealogyTreeNode,
  GenealogyTreeView,
} from './types';

/**
 * Fallback when no tenant config is passed (e.g. the unilevel view): show owner
 * + slot code, which is today's node card. Binary points + sponsor stay off.
 */
const DEFAULT_NODE_FIELDS: GenealogyNodeFields = {
  fullName: true,
  slotCode: true,
  packageAmount: false,
  leftPoints: false,
  rightPoints: false,
  sponsorName: false,
  sponsorSlotCode: false,
  walletBalance: false,
};

type GenealogyNodeProps = {
  node: GenealogyTreeNode;
  view: GenealogyTreeView;
  currency: string;
  childrenForNode: (slotId: string) => GenealogyTreeNode[];
  expanded: Map<string, boolean>;
  onToggle: (slotId: string) => void;
  onAddSlot: (ctx: AddSlotContext) => void;
  isRoot?: boolean;
  highlightSlotId?: string;
  /** When false, the "Add New Slot" action card is hidden (tenant toggle). Default true. */
  enableSlotActivation?: boolean;
  /**
   * When true, the binary L/R empty seats render as muted, non-interactive
   * placeholders (the tenant's Auto Place is on — new slots auto-place via
   * spillover, so manual seat-picking in the tree is moot). The seats are
   * ALWAYS rendered so the L/R geometry never collapses. Default false =
   * interactive "Place slot" seats.
   */
  binaryPlacementReadOnly?: boolean;
  /** Which detail fields the card shows (binary only). Default {@link DEFAULT_NODE_FIELDS}. */
  nodeFields?: GenealogyNodeFields;
};

export function GenealogyNode({
  node,
  view,
  currency,
  childrenForNode,
  expanded,
  onToggle,
  onAddSlot,
  isRoot = false,
  highlightSlotId,
  enableSlotActivation = true,
  binaryPlacementReadOnly = false,
  nodeFields = DEFAULT_NODE_FIELDS,
}: GenealogyNodeProps) {
  const isExpanded = expanded.get(node.slotId) ?? false;
  const kids = childrenForNode(node.slotId);
  // Show the expand control when the node has downline. For unilevel, the
  // children may not be loaded yet (lazy, level-by-level), so trust the
  // API-provided `childCount` — otherwise a node whose kids haven't been
  // fetched would never show an arrow to fetch them (deadlock). Binary always
  // shows it to expose the L/R placeholders.
  const hasAnyChildren = kids.length > 0 || view === 'binary' || node.childCount > 0;

  const ownerLabel = node.userFullName ?? node.userEmail ?? '—';
  const tierLabel = node.membershipName ? `${node.membershipName} PACKAGE` : 'PACKAGE';
  // null = no real rank (unranked, or the legacy placeholder DISTRIBUTOR code).
  // The row collapses rather than inventing a label — see `displayRankCode`.
  const rankLabel = displayRankCode(node.rankCode);
  const amountLabel = node.packageAmount
    ? formatMoney(new Decimal(node.packageAmount), currency)
    : null;

  // Configurable node fields — binary view only, and only when the value exists.
  // Left/Right points condense onto one row to respect the 150px card width.
  const isBinary = view === 'binary';
  const showLeftPv = isBinary && nodeFields.leftPoints && node.leftPv != null;
  const showRightPv = isBinary && nodeFields.rightPoints && node.rightPv != null;
  const pvParts: string[] = [];
  if (showLeftPv) pvParts.push(`L ${new Decimal(node.leftPv!).toFixed(2)}`);
  if (showRightPv) pvParts.push(`R ${new Decimal(node.rightPv!).toFixed(2)}`);
  const pvLabel = pvParts.length ? pvParts.join(' · ') : null;
  const showSponsorName = isBinary && nodeFields.sponsorName && !!node.sponsorName;
  const showSponsorCode = isBinary && nodeFields.sponsorSlotCode && !!node.sponsorSlotCode;
  const showWallet = isBinary && nodeFields.walletBalance && node.walletBalance != null;

  return (
    <li className={cn('mlm-tree-node', isRoot && 'mlm-tree-root')}>
      <div
        data-interactive
        data-slot-id={node.slotId}
        className={cn(
          'relative flex w-[150px] select-none flex-col items-center gap-0.5 rounded-lg border border-primary/40 bg-primary px-2 py-2 text-primary-foreground shadow-sm transition-all hover:shadow-md',
          node.slotId === highlightSlotId &&
            'ring-2 ring-offset-2 ring-amber-400 ring-offset-background',
        )}
      >
        {nodeFields.slotCode && (
          <div className="flex w-full items-center justify-center gap-1">
            <User className="h-3 w-3 shrink-0 opacity-90" />
            <span className="truncate font-mono text-[11px] font-bold">{node.slotCode}</span>
          </div>
        )}
        {nodeFields.fullName && (
          <div className="w-full truncate text-center text-[9px] opacity-90">{ownerLabel}</div>
        )}
        {rankLabel && (
          <div className="text-[9px] font-semibold uppercase tracking-wide opacity-80">
            {rankLabel}
          </div>
        )}
        <div className="text-[9px] font-semibold uppercase tracking-wide opacity-80">
          {tierLabel}
        </div>
        {nodeFields.packageAmount && amountLabel && (
          <div className="font-mono text-[11px] font-bold">{amountLabel}</div>
        )}
        {nodeFields.packageAmount && (
          <div className="text-[9px] font-semibold uppercase tracking-wide opacity-80">
            {currency}
          </div>
        )}
        {pvLabel && (
          <div className="w-full truncate text-center font-mono text-[9px] font-semibold opacity-90">
            {pvLabel}
          </div>
        )}
        {showWallet && (
          <div className="w-full truncate text-center font-mono text-[9px] font-semibold opacity-90">
            Wallet {formatMoney(new Decimal(node.walletBalance!), currency)}
          </div>
        )}
        {showSponsorName && (
          <div className="w-full truncate text-center text-[9px] opacity-80">
            Sponsor: {node.sponsorName}
          </div>
        )}
        {showSponsorCode && (
          <div className="w-full truncate text-center font-mono text-[9px] opacity-80">
            Sponsor #{node.sponsorSlotCode}
          </div>
        )}

        {hasAnyChildren && (
          <button
            type="button"
            data-interactive
            onClick={() => onToggle(node.slotId)}
            className="absolute -bottom-3 left-1/2 z-10 flex h-6 -translate-x-1/2 items-center gap-0.5 rounded-full border border-border bg-background px-1.5 text-foreground shadow-sm hover:bg-muted"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <span className="text-[10px] font-semibold">{node.childCount}</span>
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        )}
      </div>

      {isExpanded && (
        <ul>
          {view === 'binary' ? (
            <>
              {(() => {
                const left = kids.find((k) => k.position === 'L');
                const right = kids.find((k) => k.position === 'R');
                return (
                  <>
                    {left ? (
                      <GenealogyNode
                        key={left.slotId}
                        node={left}
                        view={view}
                        currency={currency}
                        childrenForNode={childrenForNode}
                        expanded={expanded}
                        onToggle={onToggle}
                        onAddSlot={onAddSlot}
                        enableSlotActivation={enableSlotActivation}
                        binaryPlacementReadOnly={binaryPlacementReadOnly}
                        highlightSlotId={highlightSlotId}
                        nodeFields={nodeFields}
                      />
                    ) : (
                      <BinaryPlaceholder
                        side="L"
                        readOnly={binaryPlacementReadOnly}
                        onClick={() =>
                          onAddSlot({
                            parentSlotId: node.slotId,
                            parentSlotCode: node.slotCode,
                            position: 'L',
                          })
                        }
                      />
                    )}
                    {right ? (
                      <GenealogyNode
                        key={right.slotId}
                        node={right}
                        view={view}
                        currency={currency}
                        childrenForNode={childrenForNode}
                        expanded={expanded}
                        onToggle={onToggle}
                        onAddSlot={onAddSlot}
                        enableSlotActivation={enableSlotActivation}
                        binaryPlacementReadOnly={binaryPlacementReadOnly}
                        highlightSlotId={highlightSlotId}
                        nodeFields={nodeFields}
                      />
                    ) : (
                      <BinaryPlaceholder
                        side="R"
                        readOnly={binaryPlacementReadOnly}
                        onClick={() =>
                          onAddSlot({
                            parentSlotId: node.slotId,
                            parentSlotCode: node.slotCode,
                            position: 'R',
                          })
                        }
                      />
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <>
              {kids.map((c) => (
                <GenealogyNode
                  key={c.slotId}
                  node={c}
                  view={view}
                  currency={currency}
                  childrenForNode={childrenForNode}
                  expanded={expanded}
                  onToggle={onToggle}
                  onAddSlot={onAddSlot}
                  enableSlotActivation={enableSlotActivation}
                  binaryPlacementReadOnly={binaryPlacementReadOnly}
                  highlightSlotId={highlightSlotId}
                  nodeFields={nodeFields}
                />
              ))}
              {enableSlotActivation && (
                <AddSlotCard
                  onClick={() =>
                    onAddSlot({
                      parentSlotId: node.slotId,
                      parentSlotCode: node.slotCode,
                      position: null,
                    })
                  }
                />
              )}
            </>
          )}
        </ul>
      )}
    </li>
  );
}

function BinaryPlaceholder({
  side,
  readOnly = false,
  onClick,
}: {
  side: 'L' | 'R';
  readOnly?: boolean;
  onClick: () => void;
}) {
  const label = side === 'L' ? 'Left' : 'Right';
  if (readOnly) {
    // Auto Place is on: the seat is still rendered so the L/R geometry stays
    // clear, but it is not clickable — placement happens automatically via
    // spillover. No `data-interactive`, so an empty seat is part of the
    // grab-to-pan surface rather than a dead button.
    return (
      <li className="mlm-tree-node">
        <div
          aria-disabled="true"
          className="flex w-[150px] cursor-default select-none flex-col items-center justify-center rounded-lg border-2 border-dashed border-border/50 bg-muted/20 py-4 text-muted-foreground/60"
        >
          <span className="text-[10px] font-semibold uppercase tracking-wide">{label}</span>
          <span className="text-[9px]">Open seat</span>
        </div>
      </li>
    );
  }
  return (
    <li className="mlm-tree-node">
      <button
        type="button"
        data-interactive
        onClick={onClick}
        className="flex w-[150px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-card py-4 text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-foreground"
      >
        <Plus className="h-4 w-4" />
        <span className="mt-1 text-[10px] font-semibold uppercase tracking-wide">{label}</span>
        <span className="text-[9px]">Place slot</span>
      </button>
    </li>
  );
}

function AddSlotCard({ onClick }: { onClick: () => void }) {
  return (
    <li className="mlm-tree-node">
      <button
        type="button"
        data-interactive
        onClick={onClick}
        className="flex w-[150px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-card py-4 text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-foreground"
      >
        <Plus className="h-4 w-4" />
        <span className="mt-1 text-[10px] font-semibold uppercase tracking-wide">
          Add New Slot
        </span>
      </button>
    </li>
  );
}
